import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { getSuburbBySlug, getNearbySuburbs, REGIONS } from "../lib/suburbs";
import Header from "../components/Header";
import Footer from "../components/Footer";
import QuoteWizard from "../components/QuoteWizard";
import { ChevronRight, ShieldCheck, Hammer, MapPin, Star, ArrowUpRight, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_fencing-hub-2/artifacts/z0ubtl0s_IMG_20260511_222829.jpg";

const MINI_GALLERY = [
  { type: "Timber", img: "https://customer-assets.emergentagent.com/job_fencing-hub-2/artifacts/30o593ao_IMG_20260511_223641.jpg" },
  { type: "Colorbond", img: "https://customer-assets.emergentagent.com/job_fencing-hub-2/artifacts/sjsiaq73_4762451974165133520.jpg" },
  { type: "Iron", img: "https://customer-assets.emergentagent.com/job_fencing-hub-2/artifacts/4qethzih_IMG_20260511_224125.jpg" },
  { type: "Aluminium", img: "https://customer-assets.emergentagent.com/job_fencing-hub-2/artifacts/p9xp6ymf_IMG_20260511_222316.jpg" },
];

const TESTIMONIALS = [
  { name: "Sarah M.", quote: "Dan turned up when he said, finished a day early, and the fence is dead straight. Best tradie experience we've had — full stop." },
  { name: "Mark D.", quote: "Quoted on a Friday, started the next Tuesday. No mess, no nonsense. Six months on the gate still works like the day it went in." },
  { name: "Linda & Pete", quote: "Got us through pool certification first time. The crew was respectful around the kids and dogs. Worth every cent." },
  { name: "Tony R.", quote: "Site fence + sliding gate for a build. On time, on price, and the boys cleaned up before they left every single day." },
  { name: "Jess H.", quote: "Heritage spear-top out the front of our place — looks incredible. Loads of compliments from neighbours already." },
];

function setMeta(title, description) {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.name = "description";
    document.head.appendChild(m);
  }
  m.content = description;
}

function injectJsonLd(data, id = "td-jsonld") {
  let s = document.getElementById(id);
  if (!s) {
    s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    document.head.appendChild(s);
  }
  s.textContent = JSON.stringify(data);
}

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = getSuburbBySlug(slug);
  const [wizardOpen, setWizardOpen] = useState(false);
  const open = () => setWizardOpen(true);

  useEffect(() => {
    if (!suburb) return;
    const title = `Fencing in ${suburb.name}, VIC — Timber, Colorbond & Iron | TradesmanDan`;
    const description = `Local fence builder in ${suburb.name}, ${suburb.postcode}. Free quotes for timber paling, Colorbond, chain link, aluminium & wrought iron fencing. 12+ years on the tools, fully insured, same-day callbacks.`;
    setMeta(title, description);
    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `https://tradesmandan.com.au/fencing/${suburb.slug}`,
      "name": "TradesmanDan Fencing",
      "image": HERO_IMG,
      "url": `https://tradesmandan.com.au/fencing/${suburb.slug}`,
      "email": "Tradesmandanvic@gmail.com",
      "areaServed": { "@type": "City", "name": suburb.name, "addressRegion": "VIC", "postalCode": suburb.postcode },
      "address": { "@type": "PostalAddress", "addressLocality": suburb.name, "addressRegion": "Victoria", "postalCode": suburb.postcode, "addressCountry": "AU" },
      "priceRange": "$$",
      "description": description,
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "80" },
    });
    return () => {
      setMeta("TradesmanDan — Fencing for SE VIC + Gippsland", "Residential, commercial & ornamental fencing across South East Victoria and Gippsland.");
    };
  }, [suburb]);

  if (!suburb) return <Navigate to="/" replace />;

  const region = REGIONS[suburb.region];
  const nearby = getNearbySuburbs(suburb.slug, 8);
  const testimonial = TESTIMONIALS[suburb.name.length % TESTIMONIALS.length];

  return (
    <div className="bg-[#0D0D0D] min-h-screen text-white">
      <Header onQuoteClick={open} />

      {/* HERO */}
      <section
        data-testid="suburb-hero"
        className="relative min-h-[85vh] grain overflow-hidden bg-[#0D0D0D]"
      >
        <img src={HERO_IMG} alt={`Fencing in ${suburb.name}`} className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-20">
          {/* breadcrumb */}
          <nav className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/60 mb-8 stamp-in">
            <Link to="/" className="hover:text-[#FF5A00]">TradesmanDan</Link>
            <span>/</span>
            <span className="text-white/40">Fencing</span>
            <span>/</span>
            <span className="text-[#FF5A00]">{suburb.name}</span>
          </nav>

          <div className="inline-flex items-center gap-3 border border-[#FF5A00] px-4 py-2 mb-8">
            <MapPin className="w-3 h-3 text-[#FF5A00]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] font-bold">
              Local fencer in {suburb.name} · {suburb.postcode}
            </span>
          </div>

          <h1 className="font-heading uppercase text-white text-5xl sm:text-6xl lg:text-[7rem] leading-[0.85] tracking-tighter max-w-5xl">
            Fencing In<br />
            <span className="text-[#FF5A00]">{suburb.name}.</span><br />
            Done Right.
          </h1>

          <p className="mt-10 text-lg lg:text-xl text-[#D4D4D4] max-w-2xl leading-relaxed font-body">
            Timber, Colorbond, chain link, aluminium &amp; wrought iron — TradesmanDan
            is the local crew {suburb.name} (and the rest of {region.label}) calls
            when a fence needs to look sharp on day one <em>and</em> still look sharp
            in fifteen years. {region.blurb}
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="suburb-hero-quote"
              onClick={open}
              className="group bg-[#FF5A00] text-black font-heading tracking-[0.2em] uppercase px-8 py-5 hover:bg-[#E65100] transition-all flex items-center justify-center gap-3 text-base"
            >
              Get My Free {suburb.name} Quote
              <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="mailto:Tradesmandanvic@gmail.com"
              className="border-2 border-white/30 text-white font-heading tracking-[0.2em] uppercase px-8 py-5 hover:border-[#FF5A00] hover:text-[#FF5A00] transition-all flex items-center justify-center gap-3 text-base"
            >
              Email The Crew
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0D0D0D] border-t border-[#222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#333]">
            {[
              { icon: ShieldCheck, label: "Fully Insured", sub: "$20M PL" },
              { icon: Hammer, label: "12+ Years", sub: "On The Tools" },
              { icon: MapPin, label: suburb.name, sub: "Local Crew" },
              { icon: Star, label: "4.9 / 5", sub: "80+ Reviews" },
            ].map((it, i) => (
              <div key={i} className="bg-[#0D0D0D] p-6 lg:p-8 flex flex-col gap-2">
                <it.icon className="w-7 h-7 text-[#FF5A00]" strokeWidth={1.5} />
                <div className="font-heading text-white text-2xl lg:text-3xl uppercase tracking-tight mt-2">{it.label}</div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#A3A3A3]">{it.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="bg-[#0D0D0D] py-24 lg:py-32 border-t border-[#222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] font-bold mb-4">
              [ 01 ] Why {suburb.name}
            </div>
            <h2 className="font-heading uppercase text-white text-4xl lg:text-6xl leading-[0.9] tracking-tighter">
              Built For<br /><span className="text-[#FF5A00]">{suburb.vibe}.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-px bg-[#333]">
            {[
              { t: `Local crew, local pricing`, d: `We're already booking jobs in ${suburb.name} this month, so there's no travel surcharge and no long lead times — we just slot you in.` },
              { t: `Built for ${region.label}`, d: region.blurb },
              { t: `Straight talk, written quotes`, d: `Free on-site measure, transparent line-item quote in your inbox, then we lock in a start date. No pressure, no surprise extras.` },
              { t: `Licensed & insured`, d: `$20M public liability, all our timber is H4-treated, and every Colorbond install ships with the proper manufacturer warranty.` },
            ].map((p, i) => (
              <div key={i} className="bg-[#0D0D0D] p-6 flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#FF5A00] shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="font-heading text-xl uppercase tracking-tight text-white">{p.t}</div>
                  <p className="text-[#A3A3A3] mt-1 leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini gallery */}
      <section className="bg-[#0D0D0D] py-24 border-t border-[#222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] font-bold mb-4">
            [ 02 ] Recent Work Near {suburb.name}
          </div>
          <h2 className="font-heading uppercase text-white text-4xl lg:text-6xl leading-[0.9] tracking-tighter mb-12">
            Real Fences.<br /><span className="text-[#FF5A00]">Real Locals.</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#333]">
            {MINI_GALLERY.map((p, i) => (
              <div key={i} data-testid={`suburb-gallery-${i}`} className="relative aspect-[4/5] bg-[#0D0D0D] overflow-hidden group">
                <img src={p.img} alt={`${p.type} fence near ${suburb.name}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/90 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 bg-[#FF5A00] text-black px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-bold">{p.type}</div>
                <div className="absolute bottom-3 left-3 right-3 font-heading uppercase text-lg tracking-tight text-white">
                  {p.type} build · {region.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-[#1A1A1A] py-20 border-t border-[#222] grain relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center relative">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-[#FF5A00] text-[#FF5A00]" />
            ))}
          </div>
          <p className="font-body text-2xl lg:text-3xl text-white leading-snug">
            "{testimonial.quote}"
          </p>
          <div className="mt-8 font-heading text-xl uppercase tracking-tight">{testimonial.name}</div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#A3A3A3] mt-1">{suburb.name}, VIC · Verified Google review</div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D0D0D] py-24 border-t border-[#222]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-heading uppercase text-white text-5xl lg:text-7xl leading-[0.9] tracking-tighter">
            Ready For A Quote<br /><span className="text-[#FF5A00]">In {suburb.name}?</span>
          </h2>
          <p className="text-[#A3A3A3] text-lg mt-6 max-w-xl mx-auto">
            Tell us about your fence in under 60 seconds — we'll get back to you the same business day.
          </p>
          <button
            data-testid="suburb-cta-quote"
            onClick={open}
            className="mt-10 bg-[#FF5A00] text-black font-heading tracking-[0.2em] uppercase px-10 py-5 hover:bg-[#E65100] transition-colors text-base"
          >
            Start My Free Quote →
          </button>
        </div>
      </section>

      {/* Nearby suburbs (internal link juice) */}
      <section className="bg-[#0D0D0D] py-20 border-t border-[#222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] font-bold mb-4">
            [ 03 ] We Also Fence
          </div>
          <h3 className="font-heading uppercase text-white text-3xl lg:text-5xl leading-[0.9] tracking-tighter mb-10">
            Suburbs Near {suburb.name}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#333]">
            {nearby.map((s) => (
              <Link
                key={s.slug}
                to={`/fencing/${s.slug}`}
                data-testid={`nearby-${s.slug}`}
                className="bg-[#0D0D0D] hover:bg-[#1A1A1A] hover:border-[#FF5A00] transition-colors p-5 flex items-center justify-between group border-l-2 border-transparent"
              >
                <div>
                  <div className="font-heading text-lg uppercase tracking-tight">{s.name}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] mt-1">{REGIONS[s.region].label}</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#666] group-hover:text-[#FF5A00]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer onQuoteClick={open} />
      <QuoteWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}
